import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, formatDateTime } from "../ui";
import { useAuth } from "../AuthContext";

export default function ForumTopic() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const baseUrl = user?.role === "admin" ? "/admin/forum" : "/portal/forum";

  const load = useCallback(
    () =>
      api
        .get(`/portal/forum/${id}`)
        .then((r) => setData(r.data))
        .catch(() => toast.error("Tópico não encontrado")),
    [id]
  );

  useEffect(() => {
    load();
  }, [load]);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/portal/forum/${id}/replies`, { body: reply });
      setReply("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const deleteReply = async (replyId) => {
    if (!window.confirm("Excluir esta resposta?")) return;
    try {
      await api.delete(`/admin/forum/replies/${replyId}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!data) return <div className="text-sm text-slate-500">Carregando...</div>;
  const { topic, replies } = data;

  return (
    <div data-testid="forum-topic-page">
      <Link
        to={baseUrl}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-[#002776]"
      >
        <ArrowLeft size={14} /> Voltar ao fórum
      </Link>

      <Card className="mt-4 p-6">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-[10px] uppercase tracking-wide">
            {topic.category}
          </span>
          <span className="text-slate-500">
            por {topic.author_name} · {formatDateTime(topic.created_at)}
          </span>
        </div>
        <h1 className="mt-3 font-serif-display text-3xl leading-tight">
          {topic.title}
        </h1>
        <p className="mt-4 whitespace-pre-line text-slate-700 leading-relaxed">
          {topic.body}
        </p>
      </Card>

      <div className="mt-8">
        <h3 className="font-serif-display text-xl">
          Respostas ({replies.length})
        </h3>
        <ul className="mt-4 space-y-3">
          {replies.map((r) => (
            <li
              key={r.id}
              data-testid={`reply-${r.id}`}
              className="bg-white border border-slate-200 p-5 rounded-md"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-medium">{r.author_name}</div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">
                    {formatDateTime(r.created_at)}
                  </span>
                  {user?.role === "admin" && (
                    <button
                      data-testid={`delete-reply-${r.id}`}
                      onClick={() => deleteReply(r.id)}
                      className="text-rose-600 hover:text-rose-700"
                      title="Excluir resposta"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-2 text-slate-700 whitespace-pre-line">{r.body}</p>
            </li>
          ))}
        </ul>
      </div>

      <Card className="mt-6 p-5">
        <form data-testid="new-reply-form" onSubmit={submitReply}>
          <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
            Responder
          </label>
          <textarea
            data-testid="new-reply-body"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[100px]"
            placeholder="Escreva sua resposta..."
            required
          />
          <button
            data-testid="new-reply-submit"
            disabled={busy}
            className="mt-3 px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
          >
            Publicar resposta
          </button>
        </form>
      </Card>
    </div>
  );
}
